import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  Image,
  Dimensions,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScrollView } from 'react-native-gesture-handler';
import { fontFamily } from '../../constants/fonts';
import { useSpeedRailzMapPromoUiEnabled } from '../../utils/speedRailzMapUi';
import { useHeavenHillsMapPromoUiEnabled } from '../../utils/heavenHillsMapUi';
import { PALOMA_NAVY } from '../../constants/palomaPaloozaBrand';

const PP_LOGO = require('../../assets/paloma-palooza-logo.png');

const screenHeight = Dimensions.get('window').height;

const BORDER_ACTIVE = '#6B4C3B';
const BG_PRESSED = '#9B7F6B';
const BG_ACTIVE = '#D4C4B8';
const BORDER_INACTIVE = '#D1D5DB';
const TEXT_MUTED = '#4B5563';

function toggleCardStyle(pressed, active) {
  if (pressed) {
    return [styles.toggleCard, styles.togglePressed];
  }
  if (active) {
    return [styles.toggleCard, styles.toggleActive];
  }
  return [styles.toggleCard, styles.toggleInactive];
}

const FilterBottomSheetContent = ({
  showOnlySpeedRailzOnMap,
  setShowOnlySpeedRailzOnMap,
  showOnlyHeavenHillsOnMap,
  setShowOnlyHeavenHillsOnMap,
  showDisgoRestaurants,
  setShowDisgoRestaurants,
  showNonDisgoRestaurants,
  setShowNonDisgoRestaurants,
  showFriendsRatedRestaurants,
  setShowFriendsRatedRestaurants,
  showMyCheckedInRestaurants,
  setShowMyCheckedInRestaurants,
  isSignedIn,
  savedPlacesFilter,
  setSavedPlacesFilter,
  savedMapFilterData,
  /** When the parent already shows a "Filters" heading (e.g. web modal header) */
  hideTitle = false,
}) => {
  const isSpeedRailzMapPromoUiEnabled = useSpeedRailzMapPromoUiEnabled();
  const isHeavenHillsMapPromoUiEnabled = useHeavenHillsMapPromoUiEnabled();
  const [isSpeedRailzPressed, setIsSpeedRailzPressed] = useState(false);
  const [isHeavenHillsPressed, setIsHeavenHillsPressed] = useState(false);
  const [isDisgoPressed, setIsDisgoPressed] = useState(false);
  const [isNonDisgoPressed, setIsNonDisgoPressed] = useState(false);
  const [isFriendsRatedPressed, setIsFriendsRatedPressed] = useState(false);
  const [isMyCheckedInPressed, setIsMyCheckedInPressed] = useState(false);
  const [isAllSavedPressed, setIsAllSavedPressed] = useState(false);
  const [listPressedId, setListPressedId] = useState(null);

  const totalSavedCount =
    savedMapFilterData?.allSavedRestaurantIds?.length ?? 0;

  const toggleAllSaved = () => {
    if (savedPlacesFilter === 'all') {
      setSavedPlacesFilter(null);
    } else {
      setSavedPlacesFilter('all');
    }
  };

  const toggleListFilter = (listId) => {
    if (String(savedPlacesFilter) === String(listId)) {
      setSavedPlacesFilter(null);
    } else {
      setSavedPlacesFilter(listId);
    }
  };

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[
        styles.scrollContent,
        { minHeight: screenHeight * 0.5 + 450 },
      ]}
      showsVerticalScrollIndicator={true}
      nestedScrollEnabled={true}
      bounces={true}
      scrollEnabled={true}
    >
      {!hideTitle ? <Text style={styles.screenTitle}>Filters</Text> : null}

      {/* SpeedRailz Toggle */}
      {isSpeedRailzMapPromoUiEnabled ? (
        <View style={styles.section}>
          <Pressable
            onPress={() => setShowOnlySpeedRailzOnMap(!showOnlySpeedRailzOnMap)}
            onPressIn={() => setIsSpeedRailzPressed(true)}
            onPressOut={() => setIsSpeedRailzPressed(false)}
            style={toggleCardStyle(
              isSpeedRailzPressed,
              showOnlySpeedRailzOnMap
            )}
          >
            <View style={styles.toggleRowStart}>
              <Image
                source={require('../../assets/sr-blk-logo-1.png')}
                style={styles.speedRailzLogo}
                resizeMode="contain"
              />
              <View style={styles.toggleTextBlock}>
                <Text style={styles.toggleTitle}>SpeedRailz</Text>
                <Text style={styles.toggleSubtitle}>
                  Show only SpeedRailz participating restaurants
                </Text>
              </View>
            </View>
          </Pressable>
        </View>
      ) : null}

      {/* Paloma Palooza map filter (Convex: heaven_hills_map_promo_ui) */}
      {isHeavenHillsMapPromoUiEnabled ? (
        <View style={styles.section}>
          <Pressable
            onPress={() => setShowOnlyHeavenHillsOnMap(!showOnlyHeavenHillsOnMap)}
            onPressIn={() => setIsHeavenHillsPressed(true)}
            onPressOut={() => setIsHeavenHillsPressed(false)}
            style={toggleCardStyle(
              isHeavenHillsPressed,
              showOnlyHeavenHillsOnMap
            )}
          >
            <View style={styles.toggleRowStart}>
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  backgroundColor: PALOMA_NAVY,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Image
                  source={PP_LOGO}
                  style={{ width: 24, height: 24 }}
                  resizeMode="contain"
                  accessibilityIgnoresInvertColors
                />
              </View>
              <View style={styles.toggleTextBlock}>
                <Text style={styles.toggleTitle}>Paloma Palooza</Text>
                <Text style={styles.toggleSubtitle}>
                  Show only Paloma Palooza participating restaurants
                </Text>
              </View>
            </View>
          </Pressable>
        </View>
      ) : null}

      {/* Disgo Restaurants Toggle */}
      <View style={styles.section}>
        <Pressable
          onPress={() => setShowDisgoRestaurants(!showDisgoRestaurants)}
          onPressIn={() => setIsDisgoPressed(true)}
          onPressOut={() => setIsDisgoPressed(false)}
          style={toggleCardStyle(isDisgoPressed, showDisgoRestaurants)}
        >
          <View style={styles.toggleRowCenter}>
            <Ionicons
              name="restaurant"
              size={28}
              color={showDisgoRestaurants ? BORDER_ACTIVE : '#9B9B9B'}
            />
            <View style={styles.toggleTextBlock}>
              <Text style={styles.toggleTitle}>Disgo Restaurants</Text>
              <Text style={styles.toggleSubtitle}>
                Show Disgo partner restaurants
              </Text>
            </View>
          </View>
        </Pressable>
      </View>

      {/* Non Disgo Restaurants Toggle */}
      <View style={styles.section}>
        <Pressable
          onPress={() => setShowNonDisgoRestaurants(!showNonDisgoRestaurants)}
          onPressIn={() => setIsNonDisgoPressed(true)}
          onPressOut={() => setIsNonDisgoPressed(false)}
          style={toggleCardStyle(isNonDisgoPressed, showNonDisgoRestaurants)}
        >
          <View style={styles.toggleRowCenter}>
            <Ionicons
              name="restaurant-outline"
              size={28}
              color={showNonDisgoRestaurants ? BORDER_ACTIVE : '#9B9B9B'}
            />
            <View style={styles.toggleTextBlock}>
              <Text style={styles.toggleTitle}>Non Disgo Restaurants</Text>
              <Text style={styles.toggleSubtitle}>
                Show non-partner restaurants
              </Text>
            </View>
          </View>
        </Pressable>
      </View>

      {/* Friends Rated Restaurants Toggle */}
      <View style={styles.section}>
        <Pressable
          onPress={() =>
            setShowFriendsRatedRestaurants(!showFriendsRatedRestaurants)
          }
          onPressIn={() => setIsFriendsRatedPressed(true)}
          onPressOut={() => setIsFriendsRatedPressed(false)}
          style={toggleCardStyle(
            isFriendsRatedPressed,
            showFriendsRatedRestaurants
          )}
        >
          <View style={styles.toggleRowCenter}>
            <Ionicons
              name="star"
              size={28}
              color={showFriendsRatedRestaurants ? BORDER_ACTIVE : '#9B9B9B'}
            />
            <View style={styles.toggleTextBlock}>
              <Text style={styles.toggleTitle}>Friends Rated Restaurants</Text>
              <Text style={styles.toggleSubtitle}>
                Show only restaurants rated by friends
              </Text>
            </View>
          </View>
        </Pressable>
      </View>

      {/* My Checked In Restaurants Toggle */}
      <View style={styles.section}>
        <Pressable
          onPress={() =>
            setShowMyCheckedInRestaurants(!showMyCheckedInRestaurants)
          }
          onPressIn={() => setIsMyCheckedInPressed(true)}
          onPressOut={() => setIsMyCheckedInPressed(false)}
          style={toggleCardStyle(
            isMyCheckedInPressed,
            showMyCheckedInRestaurants
          )}
        >
          <View style={styles.toggleRowCenter}>
            <Ionicons
              name="checkmark-circle"
              size={28}
              color={showMyCheckedInRestaurants ? BORDER_ACTIVE : '#9B9B9B'}
            />
            <View style={styles.toggleTextBlock}>
              <Text style={styles.toggleTitle}>My Checked In Restaurants</Text>
              <Text style={styles.toggleSubtitle}>
                Show only restaurants I've checked in to
              </Text>
            </View>
          </View>
        </Pressable>
      </View>

      {/* Saved places: all saved + one row per list (radio-style) */}
      {isSignedIn ? (
        <View style={styles.section}>
          <Text style={styles.savedSectionTitle}>Saved places</Text>
          <Text style={styles.savedSectionHint}>
            Show only spots you&apos;ve bookmarked. Pick one list or all saved.
          </Text>
          {savedMapFilterData === undefined ? (
            <View style={styles.savedLoading}>
              <ActivityIndicator color={BORDER_ACTIVE} />
              <Text style={[styles.toggleSubtitle, { marginLeft: 10 }]}>Loading your lists…</Text>
            </View>
          ) : (
            <>
              <Pressable
                onPress={() =>
                  totalSavedCount > 0 ? toggleAllSaved() : undefined
                }
                onPressIn={() => setIsAllSavedPressed(true)}
                onPressOut={() => setIsAllSavedPressed(false)}
                disabled={totalSavedCount === 0}
                style={toggleCardStyle(
                  isAllSavedPressed,
                  savedPlacesFilter === 'all'
                )}
              >
                <View style={styles.toggleRowCenter}>
                  <Ionicons
                    name="bookmark"
                    size={28}
                    color={
                      savedPlacesFilter === 'all'
                        ? BORDER_ACTIVE
                        : totalSavedCount === 0
                          ? '#ccc'
                          : '#9B9B9B'
                    }
                  />
                  <View style={styles.toggleTextBlock}>
                    <Text
                      style={[
                        styles.toggleTitle,
                        totalSavedCount === 0 && styles.toggleTitleDisabled,
                      ]}
                    >
                      All saved
                    </Text>
                    <Text style={styles.toggleSubtitle}>
                      {totalSavedCount === 0
                        ? 'Save places from a restaurant card to see them here'
                        : `Every place on your lists (${totalSavedCount})`}
                    </Text>
                  </View>
                </View>
              </Pressable>

              {savedMapFilterData.lists.map((list) => {
                const active = String(savedPlacesFilter) === String(list._id);
                const disabled = list.itemCount === 0;
                return (
                  <View key={String(list._id)} style={{ marginTop: 10 }}>
                    <Pressable
                      onPress={() =>
                        disabled ? undefined : toggleListFilter(list._id)
                      }
                      onPressIn={() => setListPressedId(list._id)}
                      onPressOut={() => setListPressedId(null)}
                      disabled={disabled}
                      style={toggleCardStyle(
                        listPressedId === list._id,
                        active
                      )}
                    >
                      <View style={styles.toggleRowCenter}>
                        <Ionicons
                          name="list-outline"
                          size={28}
                          color={
                            active ? BORDER_ACTIVE : disabled ? '#ccc' : '#9B9B9B'
                          }
                        />
                        <View style={styles.toggleTextBlock}>
                          <Text
                            style={[
                              styles.toggleTitle,
                              disabled && styles.toggleTitleDisabled,
                            ]}
                            numberOfLines={2}
                          >
                            {list.name}
                          </Text>
                          <Text style={styles.toggleSubtitle}>
                            {list.itemCount === 0
                              ? 'Empty list'
                              : `${list.itemCount} ${
                                  list.itemCount === 1 ? 'place' : 'places'
                                }`}
                          </Text>
                        </View>
                      </View>
                    </Pressable>
                  </View>
                );
              })}
            </>
          )}
        </View>
      ) : null}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 100,
  },
  screenTitle: {
    fontSize: 24,
    fontFamily: fontFamily.abhayaBold,
    marginBottom: 16,
    color: '#000000',
  },
  section: {
    marginBottom: 16,
  },
  toggleCard: {
    borderRadius: 6,
    padding: 8,
    borderWidth: 2,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  togglePressed: {
    backgroundColor: BG_PRESSED,
    borderColor: BORDER_ACTIVE,
  },
  toggleActive: {
    backgroundColor: BG_ACTIVE,
    borderColor: BORDER_ACTIVE,
  },
  toggleInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: BORDER_INACTIVE,
  },
  toggleRowStart: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  toggleRowCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  speedRailzLogo: {
    width: 40,
    height: 40,
  },
  toggleTextBlock: {
    marginLeft: 12,
    flex: 1,
  },
  toggleTitle: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: '#000000',
    marginVertical: 0,
    paddingVertical: 0,
  },
  toggleSubtitle: {
    fontSize: 14,
    fontFamily: fontFamily.abhaya,
    color: TEXT_MUTED,
    marginVertical: 0,
    paddingVertical: 0,
  },
  savedSectionTitle: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: '#000000',
    marginBottom: 6,
  },
  savedSectionHint: {
    fontSize: 13,
    fontFamily: fontFamily.abhaya,
    color: TEXT_MUTED,
    marginBottom: 12,
  },
  savedLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  toggleTitleDisabled: {
    color: '#9ca3af',
  },
});

export default FilterBottomSheetContent;
